import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faExclamationCircle,
    faExclamationTriangle,
    faPlusMinus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isNull } from 'lodash-es';
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { fixFloat } from '../../../graph/data-types';
import { DATA_TYPE } from '../../../graph/enums';
import classNames from '../../../utils/classNames';
import Tooltip from '../../../utils/tooltip';
import ProgressBar from '../progressBar/ProgressBar';
import { getLdenIcon, militaryToText, type TimeWindow } from '../utils';

function formatCorrection(correction: number, unit?: string): string {
    return `${correction > 0 ? '+' : ''}${correction}${unit ? ` ${unit}` : ''}`;
}

interface LevelIconProps {
    level?: number | null;
    isAlarm: boolean;
    unit?: string;
}

function LevelIcon({ level, isAlarm }: LevelIconProps) {
    if (!level) return null;

    return (
        <div className="flex content-center gap-px">
            <FontAwesomeIcon
                icon={isAlarm ? faExclamationTriangle : faExclamationCircle}
                className={classNames(
                    'h-2 w-2 rounded-full p-0.5 text-white',
                    isAlarm ? 'bg-error' : 'bg-warning'
                )}
                size="sm"
            />
            <p className="text-2xs">{level}</p>
        </div>
    );
}

interface IconWithTextProps {
    icon: IconDefinition;
    title: string;
    className: string;
}

function IconWithText({ icon, title, className }: IconWithTextProps) {
    return (
        <p>
            <FontAwesomeIcon
                icon={icon}
                className={classNames('mr-1 h-2 w-2 rounded-full p-0.5 text-white', className)}
                size="sm"
            />
            {title}
        </p>
    );
}

export interface SelectionBlockProps {
    timeWindow: TimeWindow;
    value?: number;
    unit?: string;
    isActive?: boolean;
    isCompact?: boolean;
}

const SelectionBlock = forwardRef<HTMLDivElement, SelectionBlockProps>(
    ({ timeWindow, value, unit, isActive, isCompact }, ref) => {
        const { name, warningLevel, alarmLevel, correction } = timeWindow;
        const startTime = militaryToText(timeWindow.startTime);
        const endTime = militaryToText(timeWindow.endTime);
        const measuredValue = value ? fixFloat(DATA_TYPE.LDEN, value) : null;
        const correctionFormatted = correction ? formatCorrection(correction) : '';
        const correctionFormattedWithUnit = correction ? formatCorrection(correction, unit) : '';
        const { icon, iconColor } = getLdenIcon(value, warningLevel, alarmLevel);
        const shouldShowTooltip = !(!measuredValue && !correction && !warningLevel && !alarmLevel);
        const hasMeasuredValue = !isNull(measuredValue);

        return (
            <div
                className={`flex h-20 flex-col justify-between overflow-hidden rounded border bg-white p-1 ${
                    isActive ? 'border-graph-blue' : 'border-gray-outline hover:border-graph-blue'
                }`}
            >
                <div className="flex flex-col">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-bold">{name}</div>

                        {hasMeasuredValue && (
                            <div ref={ref} className="flex min-w-0 items-center">
                                <div
                                    className={clsx('mr-2 whitespace-nowrap text-sm font-bold', {
                                        invisible: isCompact,
                                    })}
                                >
                                    {`${measuredValue} ${unit}`}
                                </div>
                                <FontAwesomeIcon
                                    icon={icon}
                                    className={clsx(iconColor, { invisible: isCompact })}
                                    size="lg"
                                />
                            </div>
                        )}
                    </div>

                    <div className="text-2xs text-menu-color">{`${startTime} - ${endTime}`}</div>
                </div>
                <Tooltip
                    disabled={!shouldShowTooltip}
                    content={
                        shouldShowTooltip && (
                            <>
                                {hasMeasuredValue && (
                                    <>
                                        <p>
                                            {gettext('MEASURED_LEVEL')}: {measuredValue} {unit}
                                            <FontAwesomeIcon
                                                icon={icon}
                                                className={iconColor}
                                                size="lg"
                                            />
                                        </p>
                                        <hr className="my-1" />
                                    </>
                                )}
                                {Boolean(correction) && (
                                    <IconWithText
                                        icon={faPlusMinus}
                                        className="bg-graph-blue"
                                        title={`${gettext(
                                            'CORRECTION'
                                        )}: ${correctionFormattedWithUnit}`}
                                    />
                                )}
                                {Boolean(warningLevel) && (
                                    <IconWithText
                                        icon={faExclamationCircle}
                                        className="bg-warning"
                                        title={`${gettext(
                                            'WARNING_LEVEL'
                                        )}: ${warningLevel} ${unit}`}
                                    />
                                )}
                                {Boolean(alarmLevel) && (
                                    <IconWithText
                                        icon={faExclamationTriangle}
                                        className="bg-error"
                                        title={`${gettext('WARNING_LEVEL')}: ${alarmLevel} ${unit}`}
                                    />
                                )}
                            </>
                        )
                    }
                    arrow={false}
                    placement="bottom-start"
                >
                    <div>
                        <div className="flex min-w-0 flex-wrap gap-1 overflow-hidden pb-0.5">
                            {Boolean(correction) && (
                                <div className="flex flex-shrink-0 content-center gap-px">
                                    <FontAwesomeIcon
                                        icon={faPlusMinus}
                                        className="bg-graph-blue h-2 w-2 rounded-full p-0.5 text-white"
                                        size="sm"
                                    />
                                    <p className="text-2xs">{correctionFormatted}</p>
                                </div>
                            )}
                            <LevelIcon level={warningLevel} unit={unit} isAlarm={false} />
                            <LevelIcon level={alarmLevel} unit={unit} isAlarm={true} />
                        </div>

                        <ProgressBar
                            alarmLevel={alarmLevel}
                            hasData={Boolean(value)}
                            warningLevel={warningLevel}
                            measuredValue={measuredValue}
                            isCompact={isCompact}
                            unit={unit}
                        />
                    </div>
                </Tooltip>
            </div>
        );
    }
);

SelectionBlock.displayName = 'SelectionBlock';

export default SelectionBlock;
